import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { RulebookResult, ValidationResult } from '../../../api/invoiceValidationClient'

/** Die Lagen, die die Seite kennt. Ein Abbruch nach dem Lesen hat keine — dann spricht der Abbruchhinweis. */
export type Outcome = 'valid' | 'invalid' | 'notChecked' | 'unreadable'

/** Aus der Antwort die eine Lage, die die Kopfzeile traegt. */
export function outcomeOf(result: ValidationResult): Outcome | null {
  if (!result.wasRead) return 'unreadable'
  if (result.verdict === 'passed') return 'valid'
  if (result.verdict === 'failed') return 'invalid'
  if (result.verdict === 'not_run') return 'notChecked'
  // Gelesen, aber Schritt 2 fand nicht statt — das sagt der Abbruchhinweis.
  return null
}

/**
 * Symbol, Symbolfarbe und Kante je Lage.
 *
 * 🔴 Die Statusfarbe steht NUR am Symbol und an der 2-px-Kante ueber der Kopfzeile — der Text bleibt in
 * `foreground`. Farbe ist nie das einzige Signal: Symbol und Wort sagen dasselbe (`DESIGN.md:58`).
 *
 * ⚠️ `notChecked` bekommt **niemals** Gruen und nie einen Haken. Ein Validator ohne Regelwerk meldet keine
 * Fehler — „nicht geprueft" darf nicht wie „gueltig" aussehen.
 */
const OUTCOME_STYLE: Record<
  Outcome,
  { icon: typeof CheckCircle2; iconClass: string; edge: string }
> = {
  // 🔴 `green-800` (Theme-Token), nicht `success`: `success` hatte auf Weiss 2.98:1 (< 3:1, gemessen 2026-10-10).
  valid: { icon: CheckCircle2, iconClass: 'text-green-800', edge: 'border-green-800' },
  invalid: {
    icon: XCircle,
    iconClass: 'text-destructive',
    edge: 'border-destructive',
  },
  notChecked: { icon: AlertTriangle, iconClass: 'text-muted-foreground', edge: 'border-border' },
  unreadable: {
    icon: XCircle,
    iconClass: 'text-destructive',
    edge: 'border-destructive',
  },
}

/**
 * Die Pluralform fuer `n` in der Sprache der Oberflaeche — `one`/`few`/`many`/`other`, wie `Intl.PluralRules` sie
 * nennt. Jede Sprache fuehrt alle vier Schluessel (Paritaet mit `de`, `locales-vollstaendig.test.ts`); `zero`/`two`
 * kennt keine der zwoelf Sprachen und faellt auf `other`.
 */
export function pluralCategory(language: string, n: number): 'one' | 'few' | 'many' | 'other' {
  let category: string
  try {
    category = new Intl.PluralRules(language).select(n)
  } catch {
    category = n === 1 ? 'one' : 'other'
  }
  return category === 'one' || category === 'few' || category === 'many' ? category : 'other'
}

export interface VerdictHeaderProps {
  outcome: Outcome
  /** Die Zahl der Befundzeilen, die der Report darunter zeigt (gleiche Meldungen zusammengefasst). */
  findingCount: number
  /** Die Zahl der Warnzeilen (zusammengefasst). */
  warningCount: number
  /** Die angewandten Regelwerke (`results[].format`). */
  rulebooks?: string[]
  /** Je Regelwerk das Urteil — daraus die Regelwerke, die etwas fanden. */
  rulebookResults?: RulebookResult[]
  /** Nur bei `not_run`: warum kein Regelwerk lief. */
  reason?: string
}

/**
 * Das Urteil als Kopfzeile der Seite — „Ungültig — 3 Befunde in extended-ctc-fr-ubl" / „Gültig — nach en16931-ubl".
 *
 * 🔴 Es ist der einzige Grund, warum jemand die Seite oeffnet: `text-2xl`, direkt unter der Erkennung, ohne
 * Pille. Die frühere Zeile „Befunde: n" geht hier auf — gezaehlt wird, was darunter steht.
 */
export function VerdictHeader({
  outcome,
  findingCount,
  warningCount,
  rulebooks,
  rulebookResults,
  reason,
}: VerdictHeaderProps) {
  const { t, i18n } = useTranslation()
  const { icon: Icon, iconClass, edge } = OUTCOME_STYLE[outcome]

  const label: Record<Outcome, string> = {
    valid: t('invoiceValidation.lage.gueltig', 'Gültig'),
    invalid: t('invoiceValidation.lage.ungueltig', 'Ungültig'),
    notChecked: t('invoiceValidation.lage.nichtGeprueft', 'Nicht geprüft'),
    unreadable: t('invoiceValidation.lage.nichtLesbar', 'Nicht lesbar'),
  }

  // Die Regelwerke, die etwas fanden — aus der Antwort je Regelwerk, nie geraten.
  const failing = (rulebookResults ?? [])
    .filter((r) => r.valid === false && r.findings.length > 0)
    .map((r) => r.rulebook)

  const parts: string[] = []
  if (outcome === 'valid' && rulebooks && rulebooks.length > 0) {
    parts.push(
      t('invoiceValidation.urteil.nach', {
        defaultValue: 'nach {{regelwerke}}',
        regelwerke: rulebooks.join(', '),
      })
    )
  }
  if (outcome === 'invalid' && findingCount > 0) {
    const category = pluralCategory(i18n.language, findingCount)
    parts.push(
      failing.length > 0
        ? t(`invoiceValidation.urteil.befundeIn.${category}`, {
            n: findingCount,
            regelwerke: failing.join(', '),
          })
        : t(`invoiceValidation.bericht.befunde.${category}`, { n: findingCount })
    )
  }
  if (outcome === 'notChecked' && reason) {
    parts.push(
      t('invoiceValidation.urteil.grund', { defaultValue: 'Grund: {{grund}}', grund: reason })
    )
  }
  if (warningCount > 0) {
    parts.push(
      t('invoiceValidation.zusammenfassung.warnungen', {
        defaultValue: 'Warnungen: {{n}}',
        n: warningCount,
      })
    )
  }

  return (
    <div
      className={`border-t-2 pt-3 ${edge}`}
      data-testid="invoice-validation-verdict-header"
      data-outcome={outcome}
    >
      {/* Ein Satz in zwei Gewichten: das Urteil kraeftig, die Begruendung leiser. Die Regelwerk-Ids haben keine
          Trennstelle — sie brechen notfalls im Wort, statt die Spalte zu sprengen. */}
      <h2 className="flex items-start gap-2 text-2xl font-semibold tracking-tight">
        <Icon className={`mt-1 h-6 w-6 shrink-0 ${iconClass}`} aria-hidden="true" />
        <span className="min-w-0 [overflow-wrap:anywhere]">
          <span data-testid="invoice-validation-verdict-label">{label[outcome]}</span>
          {parts.length > 0 && (
            <span
              className="font-normal text-muted-foreground tabular-nums"
              data-testid="invoice-validation-verdict-detail"
            >
              {` — ${parts.join(' · ')}`}
            </span>
          )}
        </span>
      </h2>
    </div>
  )
}
